import React, { useCallback, useEffect, useRef } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { WebView } from 'react-native-webview';

/**
 * Renders the Tao II document (self-contained HTML) and bridges reader controls:
 *  - fontFamily / fontSize  -> CSS variables inside the document
 *  - scrollTarget {id,nonce} -> smooth scroll to a heading anchor
 *  - onActive(id)            -> current heading while scrolling (TOC highlight)
 *
 * Web uses an <iframe srcDoc>; native uses react-native-webview (mirrors LoginMapPanel).
 */
export default function Tao2DocumentView({
  html,
  fontFamily,
  fontSize,
  scrollTarget,
  onActive,
  onReady,
  style,
}) {
  const iframeRef = useRef(null);
  const webRef = useRef(null);
  const readyRef = useRef(false);

  const postToDoc = useCallback((msg) => {
    if (Platform.OS === 'web') {
      const win = iframeRef.current?.contentWindow;
      if (win) win.postMessage(msg, '*');
    } else if (webRef.current) {
      webRef.current.injectJavaScript(
        `window.__tao2Apply(${JSON.stringify(JSON.stringify(msg))});true;`
      );
    }
  }, []);

  const sendFont = useCallback(() => {
    postToDoc({ type: 'tao2:font', family: fontFamily, size: fontSize });
  }, [postToDoc, fontFamily, fontSize]);

  // Push font changes once the document is ready (and immediately on later changes).
  useEffect(() => {
    if (readyRef.current) sendFont();
  }, [sendFont]);

  // Scroll to a heading when the target changes.
  useEffect(() => {
    if (!scrollTarget?.id) return;
    postToDoc({ type: 'tao2:scrollTo', id: scrollTarget.id });
  }, [scrollTarget, postToDoc]);

  const handleMessage = useCallback(
    (data) => {
      let msg = data;
      if (typeof msg === 'string') {
        try {
          msg = JSON.parse(msg);
        } catch {
          return;
        }
      }
      if (!msg || typeof msg !== 'object') return;
      if (msg.type === 'tao2:ready') {
        readyRef.current = true;
        sendFont();
        onReady && onReady();
      } else if (msg.type === 'tao2:active' && msg.id) {
        onActive && onActive(msg.id);
      }
    },
    [sendFont, onActive, onReady]
  );

  // Web: listen for postMessage from this iframe only.
  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    const onWinMessage = (e) => {
      if (iframeRef.current && e.source === iframeRef.current.contentWindow) {
        handleMessage(e.data);
      }
    };
    window.addEventListener('message', onWinMessage);
    return () => window.removeEventListener('message', onWinMessage);
  }, [handleMessage]);

  if (Platform.OS === 'web') {
    return (
      <View style={[styles.wrap, style]} dataSet={{ tao2DocView: 'true' }}>
        {React.createElement('iframe', {
          ref: iframeRef,
          title: 'Tao II',
          srcDoc: html,
          sandbox: 'allow-scripts allow-same-origin',
          style: {
            display: 'block',
            width: '100%',
            height: '100%',
            border: 'none',
            backgroundColor: '#f7f1e4',
          },
        })}
      </View>
    );
  }

  return (
    <View style={[styles.wrap, style]}>
      <WebView
        ref={webRef}
        originWhitelist={['*']}
        source={{ html }}
        style={styles.webview}
        javaScriptEnabled
        domStorageEnabled
        nestedScrollEnabled
        onMessage={(e) => handleMessage(e.nativeEvent.data)}
        onLoadEnd={() => {
          // Fallback in case the ready message is missed.
          if (!readyRef.current) {
            readyRef.current = true;
            sendFont();
          }
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    backgroundColor: '#f7f1e4',
  },
  webview: {
    flex: 1,
    backgroundColor: '#f7f1e4',
  },
});
